// CaseList.jsx
// Displays a list of cases with filtering and selection functionality.

// Copyright HS Analysis GmbH, 2023
// Author: Jakob Burghardt, Viktor Eberhardt

// Framework imports
import React from "react";

// MUI imports
import { makeStyles } from "@mui/styles";
import TextField from "@mui/material/TextField";

// HSA imports
import { FileViewerContext } from "../FileViewer";

const useStyles = makeStyles((theme) => ({
  item: {
    cursor: "pointer",
    backgroundColor: "transparent",
    "&:hover": {
      backgroundColor: "#0000001a",
    },
    padding: "10px",
    borderBottom: "1px solid #ccc",
  },
  selItem: {
    cursor: "pointer",
    backgroundColor: "transparent",
    "&:hover": {
      backgroundColor: "#0000001a",
    },
    padding: "8px",
    border: `2px solid ${theme.palette.primary.main}`,
  },
  list: {
    listStyle: "none",
    paddingLeft: 0,
  },
  listContainer: {
    height: "100%",
    overflow: "auto",
  },
  searchBar: {
    margin: "10px 6px 0 0",
  },
}));

export default function CaseList() {
  const FV = React.useContext(FileViewerContext);
  const classes = useStyles();
  const [filterText, setFilterText] = React.useState("");
  const [listItems, setlistItems] = React.useState([]);

  React.useEffect(() => {
    if (!("cases" in FV.caseList)) return;
    createItems();
  }, [FV.caseList, FV.caseSortBy, FV.caseSortReverse]);

  /**
   * Generates a sorted and optionally reversed list of case items from FV.caseList.cases,
   * then updates the state with the resulting list.
   *
   * Each item contains the case's id, name (constructed from case_acc, vorname, nachname),
   * creation date, and change date.
   *
   * Sorting is performed based on the current FV.caseSortBy value:
   * - FV.SortByType.Date: sorts by creationDateTime
   * - FV.SortByType.ChangeDate: sorts by changeDateTime
   * - otherwise: sorts by name
   *
   * If FV.caseSortReverse is true, the sorted list is reversed.
   */
  const createItems = () => {
    const items = [];
    FV.caseList.cases.forEach((c) => {
      let caseName = c.data.case_acc;
      if (c.data.vorname) caseName += " " + c.data.vorname;
      if (c.data.nachname) caseName += " " + c.data.nachname;
      items.push({
        id: c.data.Id,
        name: caseName,
        creationDateTime: c.data.CreationDateTime,
        changeDateTime: c.data.ChangeDateTime,
        filePaths: c.filePaths || [],
      });
    });
    const sortedItems = items.sort((a, b) => {
      if (FV.caseSortBy === FV.SortByType.Date)
        return a.creationDateTime.localeCompare(b.creationDateTime);
      if (FV.caseSortBy === FV.SortByType.ChangeDate) {
        return a.changeDateTime.localeCompare(b.changeDateTime);
      }
      return a.name.localeCompare(b.name);
    });
    if (FV.caseSortReverse) sortedItems.reverse();
    setlistItems(items);
  };

  /**
   * Filters the list of items based on the provided filter text.
   * Returns a new array containing only the items whose `name` property
   * includes the `filterText` (case-insensitive).
   *
   * @type {Array<Object>}
   * @property {string} name - The name property of each item in the list.
   */
  const filteredItems = React.useMemo(
    () =>
      listItems.filter((item) => {
        const itemName = item.name
          .toLowerCase()
          .includes(filterText.toLowerCase());
        return itemName;
      }),
    [listItems, filterText]
  );

  /**
   * Handles the event when a case item is clicked.
   * Updates the selected case, file path list, tree width, and triggers a renderer reset.
   *
   * @param {string|number} caseId - The unique identifier of the clicked case.
   */
  const handleItemClick = (caseId) => {
    if (FV.caseId === caseId) return;
    let clickedCase = FV.caseList.cases.find((obj) => {
      return obj.data.Id === caseId;
    });
    let pathList = [];
    for (let path of clickedCase.filePaths) {
      pathList.push({ path: path, type: "file" });
    }

    FV.setCaseId(caseId);
    clickedCase.data.Files = clickedCase.filePaths;
    FV.setSelCase(clickedCase.data);
    FV.setTreeWidth(870);
    FV.setPathList(pathList);
    FV.resetRenderer();
  };

  return (
    <div>
      <div className={classes.searchBar}>
        <TextField
          label="Filter by file name"
          variant="outlined"
          size="small"
          fullWidth
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
        />
      </div>
      {"cases" in FV.caseList && (
        <div className={classes.listContainer}>
          <ul className={classes.list}>
            {filteredItems.map((item) => (
              <li
                key={item.id}
                onClick={() => handleItemClick(item.id)}
                className={
                  FV.caseId === item.id ? classes.selItem : classes.item
                }
              >
                {item.name} ({item.filePaths?.length})
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
